/**
 * MultiattackTargetingPopup Component — Canonical Multi-Targeting UI
 * 
 * @date_of_change: 2025-09-14
 * @purpose:
 *   - This popup is now the single, canonical UI for selecting targets for ANY action
 *     that requires you to identify multiple targets — not just monster "Multiattack".
 *   - Examples: multi-ray spells (Scorching Ray), multi-dart spells (Magic Missile),
 *     class features that specify multiple discrete targets, or any action with
 *     requiredTargets > 1.
 * 
 * @what_changed:
 *   - Generalized from multiattack-specific to general multi-targeting while preserving
 *     multiattack workflows. File name retained to avoid churn; semantics updated.
 *   - Added SpellResolver-based detection for multi-component spells (via SpellHandler.resolve),
 *     and a focused Magic Missile allocation panel allowing GMs/players to direct darts per target.
 *   - Confirm now passes a payload to parent with optional { dartAllocation } which flows to
 *     ActionEconomyProcessor → CombatManager without reversions.
 * 
 * @rule_reference: AGENTS.md (targeting first-class, clear audit/logs). D&D 5e (2014/2024) RAW:
 *   Magic Missile auto-hits, 3 darts at 1st-level and +1 per slot above, each 1d4+1 force and may
 *   be divided among targets. We provide allocation controls consistent with RAW.
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Crosshair, Swords } from 'lucide-react';
import { DEBUG_ENABLED } from '../../utils/DebugLogger';
import PropTypes from 'prop-types';
import './MultiattackTargetingPopup.css';
import { SpellHandler } from '../../handlers/SpellHandler';
import { resolveSpellLevel } from '../../utils/combat/CombatantNormalizer';
// @date_of_change 2026-07-11 - Pre-roll advantage/Sneak Attack preview shares the
// engine derivation (CombatManager.previewAttackConditions) used by executeAttack.
import CombatManager from '../../utils/CombatManager';
import { selectCanonicalCombatantToken } from '../../utils/battleRoom/BattleRoomCombatantSelectors';
import { isSceneObjectToken, isUseObjectAction } from '../../utils/combat/ObjectInteractionTargeting';
import ObjectTargetNameField from './ObjectTargetNameField';
import { canTripSize, CUNNING_STRIKE_EFFECTS } from '../../utils/combat/RogueCombatFeatures';

// A stable default prevents the position synchronization effect from seeing a
// new object on every local-state render (for example, when selecting a
// Cunning Strike rider).
// date_of_change: 2026-07-18
const DEFAULT_POPUP_POSITION = Object.freeze({ x: 200, y: 150 });

// Keep the fallback list aligned with the combat engine without treating every
// targeted utility action as an attack. Generic spell/feature range validation
// belongs to those actions' resolvers; weapons and explicitly modeled attacks can
// use CombatManager's canonical attack preview safely. date_of_change: 2026-09-08
const hasExplicitAttackTargeting = (action) => {
  const actionType = String(action?.type || action?.kind || '').toLowerCase();
  const attackType = String(action?.attackType || action?.attack?.type || '').toLowerCase();
  return actionType === 'weapon'
    || actionType === 'attack'
    || action?.requiresAttackRoll === true
    || attackType.includes('melee')
    || attackType.includes('ranged');
};

const MultiattackTargetingPopup = ({
  visible,
  onClose,
  onTogglePin,
  onPositionChange,
  position = DEFAULT_POPUP_POSITION,
  isPinned = false,
  renderInline = false,
  // Token and action data
  currentCombatant,
  availableActions = [],
  // Targeting state
  selectedTargets = [],
  multiattackTarget = null,
  targetingEnabled = false,
  armedAction = null,
  // Token lookup for name resolution
  tokens = [],
  // @date_of_change 2026-07-11 - Grid/token context for pre-roll adjudication preview
  // Shape: { tokens, gridSize, gridType, feetPerCell } (same contract as combat execution)
  combatState = null,
  // Callbacks
  onArmAction,
  onConfirmArmed,
  onCancelArmed,
  onTargetSelect,
  onTargetDeselect,
  // Multiattack state
  multiattackSequence = [],
  isInMultiattackMode = false,
  onStartMultiattack,
  onAddToMultiattack,
  onExecuteMultiattack,
  onClearMultiattack,
  // @date_of_change 2026-07-12 - Inline manual-roll step (replaces native window.prompt /
  // separate centered modal for the attack roll + damage roll flow). Shape matches the
  // BattleRoom manualModal queue entry: { visible, title, prompt, numDice, dieSize }.
  pendingManualRoll = null,
  onManualRollConfirm,
  onManualRollCancel
}) => {
  const [popupPosition, setPopupPosition] = useState(position);
  const [isDragging, setIsDragging] = useState(false);
  // @date_of_change: 2025-09-14 - Local state for multi-dart/ray allocation (Magic Missile, etc.)
  const [allocation, setAllocation] = useState({}); // { targetId: count }
  // @date_of_change: 2026-07-12 - Local state for inline manual-roll entry fields
  const [manualRollValues, setManualRollValues] = useState([]);
  const [objectNameInput, setObjectNameInput] = useState('');
  const [cunningStrikeEffect, setCunningStrikeEffect] = useState('');
  const [resourceSpend, setResourceSpend] = useState('');
  const popupRef = useRef(null);
  const dragStateRef = useRef(null);
  const objectTargeting = isUseObjectAction(armedAction?.action || armedAction);
  const spendAction = armedAction?.action || armedAction;
  const resourceSpendControl = useMemo(() => {
    const config = spendAction?.mechanics?.resourceSpend;
    if (!config || typeof config !== 'object') return null;

    const resourceKey = spendAction?.mechanics?.resourceKey;
    const liveResource = resourceKey
      ? (armedAction?.attacker?.combatResources?.[resourceKey]
        || currentCombatant?.combatResources?.[resourceKey])
      : null;
    const available = Number(liveResource?.current ?? spendAction?.uses?.current);
    const configuredMin = Number(config.min);
    const configuredMax = Number(config.max);
    const min = Number.isFinite(configuredMin) ? Math.max(1, Math.floor(configuredMin)) : 1;
    const exhausted = Number.isFinite(available) && available <= 0;
    const finiteLimits = [configuredMax, available].filter(Number.isFinite);
    const max = exhausted
      ? 0
      : Math.max(min, Math.floor(finiteLimits.length > 0 ? Math.min(...finiteLimits) : min));
    const configuredDefault = Number(config.default);
    const defaultValue = exhausted
      ? 0
      : Math.min(max, Math.max(min, Number.isFinite(configuredDefault) ? Math.floor(configuredDefault) : max));

    return {
      type: config.type || 'resource',
      label: config.label || 'Resource points to spend',
      min,
      max,
      defaultValue,
      resourceKey,
      exhausted
    };
  }, [armedAction, currentCombatant, spendAction]);
  const targetChoiceState = useMemo(() => {
    if (!armedAction || typeof onTargetSelect !== 'function') {
      return { options: [], rangeFiltered: false };
    }

    const action = armedAction?.action || armedAction;
    const attackerId = armedAction?.attackerId || armedAction?.attacker?.id || action?.attackerId;
    const allowSelf = armedAction?.allowSelf === true || action?.allowSelf === true;
    const stateTokens = Array.isArray(combatState?.tokens) && combatState.tokens.length > 0
      ? combatState.tokens
      : (Array.isArray(tokens) ? tokens : []);
    const attacker = selectCanonicalCombatantToken(
      stateTokens,
      attackerId || armedAction?.attacker || currentCombatant
    );
    const candidates = stateTokens.filter((token) => {
      if (!token?.id || (!allowSelf && attacker && token.id === attacker.id)) return false;
      if (!token?.id || (!allowSelf && !attacker && token.id === attackerId)) return false;
      return objectTargeting ? isSceneObjectToken(token) : !isSceneObjectToken(token);
    });

    const hasGridContext = Boolean(combatState?.gridType)
      && Number.isFinite(combatState?.feetPerCell)
      && Number.isFinite(combatState?.gridSize);
    const rangeFiltered = !objectTargeting
      && hasExplicitAttackTargeting(action)
      && Boolean(attacker)
      && hasGridContext;
    const legalCandidates = rangeFiltered
      ? candidates.filter((target) => {
        try {
          const preview = CombatManager.previewAttackConditions(
            attacker,
            target,
            action,
            { ...combatState, tokens: stateTokens },
            {}
          );
          return preview?.validation?.valid === true;
        } catch (error) {
          // A failed preview must not silently remove a potentially legal target.
          if (DEBUG_ENABLED) console.debug('[MultiattackTargetingPopup] Target eligibility preview failed:', error?.message || error);
          return true;
        }
      })
      : candidates;
    const totals = legalCandidates.reduce((counts, token) => {
      const name = String(token?.name || 'Unnamed target');
      counts[name] = (counts[name] || 0) + 1;
      return counts;
    }, {});
    const occurrences = {};

    const options = legalCandidates.map((token) => {
      const name = String(token?.name || 'Unnamed target');
      occurrences[name] = (occurrences[name] || 0) + 1;
      const numberedName = totals[name] > 1 ? `${name} ${occurrences[name]}` : name;
      const hp = token?.hpObj ?? token?.entity?.hpObj;
      const currentHp = Number(hp?.current ?? token?.hp);
      const maxHp = Number(hp?.max ?? token?.maxHp);
      const hpLabel = Number.isFinite(currentHp) && Number.isFinite(maxHp)
        ? ` · HP ${currentHp}/${maxHp}`
        : '';
      const selected = selectedTargets.some((target) => (target?.id || target) === token.id);
      return {
        id: token.id,
        label: `${numberedName}${hpLabel}`,
        selected
      };
    });
    return { options, rangeFiltered };
  }, [armedAction, combatState, currentCombatant, objectTargeting, onTargetSelect, selectedTargets, tokens]);
  const availableTargetOptions = targetChoiceState.options;
  const targetPrompt = targetChoiceState.rangeFiltered
    ? (availableTargetOptions.length === 0
      ? 'No targets are in range. Move closer or cancel this action.'
      : `${availableTargetOptions.length} target${availableTargetOptions.length === 1 ? ' is' : 's are'} in range. Click ${availableTargetOptions.length === 1 ? 'it' : 'one'} on the map.`)
    : 'Click a valid token on the map.';

  // Reset the inline roll fields whenever a new manual-roll request arrives
  useEffect(() => {
    if (pendingManualRoll?.visible) {
      const count = Math.max(1, Math.min(100, Number(pendingManualRoll.numDice) || 1));
      setManualRollValues(Array.from({ length: count }, () => ''));
    }
  }, [pendingManualRoll]);

  useEffect(() => {
    setObjectNameInput(String(armedAction?.action?.objectName || armedAction?.objectName || ''));
    setCunningStrikeEffect('');
    setResourceSpend(resourceSpendControl ? String(resourceSpendControl.defaultValue) : '');
  }, [armedAction, resourceSpendControl]);

  const clampPopupPosition = useCallback((nextPosition) => {
    if (renderInline || typeof window === 'undefined') return nextPosition;
    const rect = popupRef.current?.getBoundingClientRect();
    const width = rect?.width || 344;
    const height = rect?.height || 220;
    const margin = 8;
    return {
      x: Math.min(Math.max(margin, nextPosition.x), Math.max(margin, window.innerWidth - width - margin)),
      y: Math.min(Math.max(margin, nextPosition.y), Math.max(margin, window.innerHeight - height - margin))
    };
  }, [renderInline]);

  // Keep a BattleRoom-owned position so closing/reopening the floating tool
  // does not move it back over the map. date_of_change: 2026-07-27
  useEffect(() => {
    setPopupPosition(position);
  }, [position]);

  useEffect(() => {
    if (!visible || renderInline || typeof window === 'undefined') return undefined;
    const keepInViewport = () => {
      setPopupPosition((previous) => {
        const next = clampPopupPosition(previous);
        if (next.x !== previous.x || next.y !== previous.y) onPositionChange?.(next);
        return next;
      });
    };
    const frame = window.requestAnimationFrame(keepInViewport);
    window.addEventListener('resize', keepInViewport);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', keepInViewport);
    };
  }, [visible, renderInline, onPositionChange, clampPopupPosition]);

  // Pointer capture gives mouse, pen, and touch the same reliable drag path.
  // Only the compact header captures the active pointer, leaving the map usable.
  // date_of_change: 2026-07-27
  const handlePointerDown = (event) => {
    if (renderInline || event.target.closest('button')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const rect = popupRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragStateRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top
    };
    setIsDragging(true);
    event.preventDefault();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (_) {}
  };

  const handlePointerMove = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    setPopupPosition(clampPopupPosition({
      x: event.clientX - dragState.offsetX,
      y: event.clientY - dragState.offsetY
    }));
  };

  const finishPointerDrag = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    dragStateRef.current = null;
    setIsDragging(false);
    setPopupPosition((current) => {
      const next = clampPopupPosition(current);
      onPositionChange?.(next);
      return next;
    });
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_) {}
  };

  const handleHeaderKeyDown = (event) => {
    if (renderInline || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      return;
    }
    const step = event.shiftKey ? 48 : 16;
    const delta = {
      ArrowLeft: { x: -step, y: 0 },
      ArrowRight: { x: step, y: 0 },
      ArrowUp: { x: 0, y: -step },
      ArrowDown: { x: 0, y: step }
    }[event.key];
    event.preventDefault();
    setPopupPosition((current) => {
      const next = clampPopupPosition({
        x: current.x + delta.x,
        y: current.y + delta.y
      });
      onPositionChange?.(next);
      return next;
    });
  };

  // Handle pin toggle
  const handleTogglePin = () => {
    if (onTogglePin) {
      onTogglePin();
    }
  };

  // Note: This popup no longer lists/selects actions; it focuses on targeting.
  // Any action-selection UI should live in the canonical ActionTracker. (lint fix)

  // Derive structured spell meta (idempotent; used for UI only)
  // We deliberately compute here for UX rendering, and again in the processor for execution.
  // Keeping both avoids coupling UI to execution internals and preserves determinism.
  const resolverMeta = useMemo(() => {
    if (!armedAction?.action) return null;
    if (armedAction.action.type !== 'spell') return null;
    const lvl = resolveSpellLevel(armedAction.action.spellLevel, armedAction.action.level, 1);
    return SpellHandler.resolve(armedAction.action, lvl);
  }, [armedAction]);

  // @date_of_change 2026-07-11 - Pre-roll adjudication preview per selected target.
  // Uses the exact derivation executeAttack applies (previewAttackConditions), so the
  // popup can show whether advantage/disadvantage is expected and whether Sneak Attack
  // is expected to apply, with the reasons. Labeled "expected" in the UI because GM
  // overrides or state changes between preview and execution remain authoritative.
  const attackPreviews = useMemo(() => {
    try {
      const action = armedAction?.action || armedAction;
      if (!action || !combatState) return {};
      if (objectTargeting) return {};
      // Auto-hit spells (e.g., Magic Missile) make no attack roll — nothing to preview.
      if (resolverMeta && resolverMeta.kind === 'auto_hit_multi_dart') return {};
      const stateTokens = Array.isArray(combatState.tokens) && combatState.tokens.length > 0
        ? combatState.tokens
        : tokens;
      const attackerId = armedAction?.attackerId || armedAction?.attacker?.id || action?.attackerId || currentCombatant?.id;
      // Initiative/armed-action ids are references only. Always preview with the
      // current canonical token so turn effects (Steady Aim, Hidden, conditions)
      // cannot be lost to a stale combatant projection.
      // date_of_change: 2026-07-15
      const attacker = selectCanonicalCombatantToken(
        stateTokens,
        attackerId || currentCombatant
      );
      if (!attacker) return {};
      const battleState = { ...combatState, tokens: stateTokens };
      const candidates = [...selectedTargets, ...(multiattackTarget ? [multiattackTarget] : [])];
      const previews = {};
      candidates.forEach((entry) => {
        const tid = (entry && typeof entry === 'object') ? entry.id : entry;
        if (!tid || previews[tid]) return;
        const targetToken = selectCanonicalCombatantToken(stateTokens, tid)
          || ((entry && typeof entry === 'object' && entry.name) ? entry : null);
        if (!targetToken) return;
        try {
          previews[tid] = CombatManager.previewAttackConditions(attacker, targetToken, action, battleState, {});
        } catch (error) {
          if (DEBUG_ENABLED) console.debug('[MultiattackTargetingPopup] Attack preview failed:', error?.message || error);
        }
      });
      return previews;
    } catch (error) {
      if (DEBUG_ENABLED) console.debug('[MultiattackTargetingPopup] Attack preview unavailable:', error?.message || error);
      return {};
    }
  }, [armedAction, combatState, selectedTargets, multiattackTarget, tokens, currentCombatant, resolverMeta, objectTargeting]);

  // @date_of_change 2026-07-11 - Render the expected roll modifier and Sneak Attack
  // adjudication for one target. Certainty language is deliberate ("expected"):
  // the final roll is adjudicated at execution time.
  const renderAttackPreview = (targetId) => {
    const preview = attackPreviews[targetId];
    if (!preview) return null;
    const bothCancel = preview.advantage && preview.disadvantage;
    const rollClass = preview.netAdvantage ? 'advantage' : (preview.netDisadvantage ? 'disadvantage' : 'neutral');
    let rollText;
    if (preview.netAdvantage) {
      rollText = `Advantage expected — ${preview.advantageSources.join(', ')}`;
    } else if (preview.netDisadvantage) {
      rollText = `Disadvantage expected — ${preview.disadvantageSources.join(', ')}`;
    } else if (bothCancel) {
      rollText = `Advantage (${preview.advantageSources.join(', ')}) and disadvantage (${preview.disadvantageSources.join(', ')}) cancel — straight roll`;
    } else {
      rollText = 'Straight roll — no advantage or disadvantage sources detected';
    }
    const sneak = preview.sneakAttack;
    return (
      <div className="attack-preview">
        <div className={`preview-roll ${rollClass}`}>
          <span className="preview-icon">{preview.netAdvantage ? '▲' : (preview.netDisadvantage ? '▼' : '•')}</span>
          {rollText}
        </div>
        {sneak?.featurePresent && (
          <div className={`preview-sneak ${sneak.eligible ? 'eligible' : 'ineligible'}`}>
            <span className="preview-icon" aria-hidden="true"><Swords size={13} /></span>
            {sneak.eligible
              ? `Sneak Attack +${sneak.dice} expected — ${sneak.reasons.join('; ')}`
              : `Sneak Attack not expected — ${sneak.blockers.join('; ') || 'requirements not met'}`}
          </div>
        )}
      </div>
    );
  };

  const selectedPreviewId = selectedTargets.length === 1
    ? (selectedTargets[0]?.id || selectedTargets[0])
    : (multiattackTarget?.id || null);
  const selectedAttackPreview = selectedPreviewId ? attackPreviews[selectedPreviewId] : null;
  const selectedAttackTarget = selectedPreviewId
    ? selectCanonicalCombatantToken(tokens, selectedPreviewId)
    : null;
  const tripEligibility = canTripSize(selectedAttackTarget?.size);
  const cunningStrikeAvailable = Boolean(
    selectedAttackPreview?.sneakAttack?.eligible
    && selectedAttackPreview?.cunningStrike?.featurePresent
    && selectedAttackPreview?.sneakAttack?.diceCount >= 1
  );

  useEffect(() => {
    if (!cunningStrikeAvailable) setCunningStrikeEffect('');
  }, [cunningStrikeAvailable, selectedPreviewId]);

  // When targets change and we are in a multi-component auto-hit spell (Magic Missile),
  // seed an even allocation and keep it within the total components.
  useEffect(() => {
    if (!resolverMeta || resolverMeta.kind !== 'auto_hit_multi_dart') return;
    const slotLevel = resolverMeta.slotLevel || 1;
    const totalDarts = (resolverMeta.multiComponent?.baseCount || 0) + Math.max(0, slotLevel - 1) * (resolverMeta.multiComponent?.perSlot || 0);
    const ids = selectedTargets.map(t => t.id || t);
    if (ids.length === 0 || totalDarts <= 0) {
      setAllocation({});
      return;
    }
    // Even distribution with remainder to earliest
    const base = Math.floor(totalDarts / ids.length);
    let remainder = totalDarts % ids.length;
    const nextAlloc = {};
    ids.forEach(id => {
      nextAlloc[id] = base + (remainder > 0 ? 1 : 0);
      if (remainder > 0) remainder--;
    });
    setAllocation(nextAlloc);
  }, [resolverMeta, selectedTargets]);

  // Allocation mutators for Magic Missile UI
  const adjustAllocation = (targetId, delta) => {
    setAllocation(prev => {
      const slotLevel = resolverMeta?.slotLevel || 1;
      const totalDarts = (resolverMeta?.multiComponent?.baseCount || 0) + Math.max(0, slotLevel - 1) * (resolverMeta?.multiComponent?.perSlot || 0);
      const current = prev[targetId] || 0;
      let next = { ...prev, [targetId]: Math.max(0, current + delta) };
      let nextSum = Object.values(next).reduce((s, n) => s + (n || 0), 0);
      // Clamp to totalDarts by undoing the change if it exceeds
      if (nextSum > totalDarts) return prev;
      return next;
    });
  };

  // Handle target confirmation (now returns optional payload: { dartAllocation })
  // @date_of_change 2025-09-14 - Add collapsible instructions state
  const [instructionsExpanded, setInstructionsExpanded] = useState(false);

  // @date_of_change 2025-09-14 - Helper function to resolve target names from IDs
  const resolveTargetName = (target) => {
    if (typeof target === 'string') {
      // Target is an ID, look up the token
      const token = tokens.find(t => t.id === target);
      return token ? token.name : 'Unknown target'; // Never expose raw UUIDs in targeting UI. date_of_change: 2026-06-23
    } else if (target && typeof target === 'object') {
      // Target is already a token object
      return target.name || 'Unknown target';
    }
    return target;
  };

  // @date_of_change 2025-09-10 - CRITICAL FIX: Consider both selectedTargets and multiattackTarget
  const handleConfirmTargeting = () => {
    // @date_of_change: 2025-09-11 - DEBUG: Log all props received by popup component
    if (DEBUG_ENABLED) console.debug('[MultiattackTargetingPopup] POPUP_PROPS_DEBUG:');
    if (DEBUG_ENABLED) console.debug(`[MultiattackTargetingPopup] - multiattackTarget:`, multiattackTarget);
    if (DEBUG_ENABLED) console.debug(`[MultiattackTargetingPopup] - selectedTargets:`, selectedTargets);
    if (DEBUG_ENABLED) console.debug(`[MultiattackTargetingPopup] - armedAction:`, armedAction);
    if (DEBUG_ENABLED) console.debug(`[MultiattackTargetingPopup] - targetingEnabled:`, targetingEnabled);
    
    const hasRegularTargets = selectedTargets.length > 0;
    const hasMultiattackTarget = multiattackTarget !== null;
    const namedObject = objectTargeting ? objectNameInput.trim() : '';
    const hasAnyTargets = hasRegularTargets || hasMultiattackTarget || Boolean(namedObject);
    
    if (armedAction && hasAnyTargets) {
      // @date_of_change 2025-09-07 - Better debugging of armed action structure
      if (DEBUG_ENABLED) console.debug('[MultiattackTargetingPopup] Armed action full object:', armedAction);
      if (DEBUG_ENABLED) console.debug('[MultiattackTargetingPopup] Action name from armedAction.action:', armedAction.action?.name);
      if (DEBUG_ENABLED) console.debug('[MultiattackTargetingPopup] Selected targets:', selectedTargets);
      if (DEBUG_ENABLED) console.debug('[MultiattackTargetingPopup] Multiattack target:', multiattackTarget?.name);
      if (DEBUG_ENABLED) console.debug('[MultiattackTargetingPopup] Confirming action:', armedAction.action?.name || armedAction.name, 'on', hasRegularTargets ? `${selectedTargets.length} regular targets` : `multiattack target: ${multiattackTarget?.name}`);
      if (onConfirmArmed) {
        // If this is a multi-dart auto-hit spell, include dart allocation payload.
        const payload = {};
        if (resolverMeta && resolverMeta.kind === 'auto_hit_multi_dart') payload.dartAllocation = allocation;
        if (namedObject) payload.objectName = namedObject;
        if (resourceSpendControl) {
          const requestedSpend = Math.floor(Number(resourceSpend));
          if (!Number.isFinite(requestedSpend) || requestedSpend < resourceSpendControl.min || requestedSpend > resourceSpendControl.max) {
            return;
          }
          payload.resourceSpend = requestedSpend;
        }
        if (cunningStrikeEffect) {
          payload.cunningStrike = {
            effect: cunningStrikeEffect,
            diceCost: CUNNING_STRIKE_EFFECTS[cunningStrikeEffect].diceCost
          };
        }
        onConfirmArmed(Object.keys(payload).length > 0 ? payload : undefined);
      }
    }
  };

  if (!visible) return null;

  // Get available actions for current combatant (not used here; targeting only)
  // const combatantActions = currentCombatant?.actions || availableActions; // (lint fix: unused)

  const modalContent = (
    <div 
      ref={popupRef}
      className={`multiattack-targeting-popup ${isDragging ? 'dragging' : ''}`}
      style={{
        left: `${popupPosition.x}px`,
        top: `${popupPosition.y}px`,
        position: 'fixed',
        zIndex: 'var(--ui-layer-floating, 5000)'
      }}
      role="dialog"
      aria-label={objectTargeting ? 'Object targeting' : 'Action targeting'}
    >
      {/* Header */}
      <div
        className="multiattack-targeting-popup__header"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointerDrag}
        onPointerCancel={finishPointerDrag}
        onKeyDown={handleHeaderKeyDown}
        tabIndex={renderInline ? -1 : 0}
        aria-label={renderInline ? undefined : 'Move targeting tool. Use arrow keys or drag.'}
        title={renderInline ? undefined : 'Drag to move targeting tool'}
      >
        <div className="header-title">
          {!renderInline && <span className="drag-affordance" aria-hidden="true" />}
          <span>{objectTargeting ? 'Object Targeting' : 'Action Targeting'}</span>
          {currentCombatant && (
            <span className="combatant-name">- {currentCombatant.name}</span>
          )}
        </div>
        <div className="header-controls">
          <button 
            className="pin-button"
            onClick={handleTogglePin}
            title={isPinned ? "Unpin from panel" : "Pin to left panel"}
            aria-label={isPinned ? 'Unpin targeting panel' : 'Pin targeting panel'}
            aria-pressed={isPinned}
          >
            {isPinned ? 'Unpin' : 'Pin'}
          </button>
          <button 
            className="close-button"
            onClick={onClose}
            title="Close"
            aria-label="Close targeting panel"
          >
            ×
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="multiattack-content">
        {/* @date_of_change 2026-07-12 - Inline manual-roll step. Replaces the native
            window.prompt/browser-dialog flow for the attack roll and (if it hits) the weapon
            damage roll: the player enters the raw die face(s) right here in the same panel
            they just clicked "Execute Action" in, instead of a separate popup. */}
        {pendingManualRoll?.visible && (
          <div className="armed-action-section manual-roll-section">
            <h4>Manual Roll: {pendingManualRoll.title}</h4>
            <p style={{ margin: '4px 0 8px', fontSize: '0.85em', opacity: 0.85 }}>
              {pendingManualRoll.prompt}
            </p>
            <div className="manual-roll-inputs" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {manualRollValues.map((val, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ minWidth: 60, fontSize: '0.85em' }}>
                    {manualRollValues.length > 1 ? `Roll ${idx + 1}` : 'Roll'}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={pendingManualRoll.dieSize || 20}
                    value={val}
                    autoFocus={idx === 0}
                    placeholder={`1-${pendingManualRoll.dieSize || 20}`}
                    onChange={(e) => {
                      const next = [...manualRollValues];
                      next[idx] = e.target.value;
                      setManualRollValues(next);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && onManualRollConfirm) {
                        const vals = manualRollValues.map((v) => {
                          const n = Number(v);
                          const size = pendingManualRoll.dieSize || 20;
                          return Number.isFinite(n) ? Math.max(1, Math.min(size, Math.floor(n))) : 1;
                        });
                        onManualRollConfirm(vals);
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                </div>
              ))}
            </div>
            <div className="targeting-status">
              <button
                className="confirm-targeting-btn"
                onClick={() => {
                  const size = pendingManualRoll.dieSize || 20;
                  const vals = manualRollValues.map((v) => {
                    const n = Number(v);
                    return Number.isFinite(n) ? Math.max(1, Math.min(size, Math.floor(n))) : 1;
                  });
                  if (onManualRollConfirm) onManualRollConfirm(vals);
                }}
              >
                Confirm Roll
              </button>
              <button
                className="cancel-targeting-btn"
                onClick={() => onManualRollCancel && onManualRollCancel()}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Current Armed Action */}
        {!pendingManualRoll?.visible && armedAction && (
          <div className="armed-action-section">
            <h4>Armed Action</h4>
            <div className="armed-action-display">
              <div className="action-name">{armedAction.action?.name || armedAction.name}</div>
              <div className="action-details">
                {(armedAction.action?.damage || armedAction.damage) && <span>Damage: {(() => {
                  // @date_of_change 2025-09-14 - Fix React rendering error by properly formatting damage objects
                  const damage = armedAction.action?.damage || armedAction.damage;
                  if (typeof damage === 'string') return damage;
                  if (Array.isArray(damage)) {
                    return damage.map(d => {
                      const formula = d.formula || d.dice || '';
                      const type = d.type || '';
                      return `${formula} ${type}`.trim();
                    }).join(', ');
                  }
                  return String(damage);
                })()}</span>}
                {(armedAction.action?.range || armedAction.range) && <span>Range: {(() => {
                  // @date_of_change 2025-09-14 - Fix React rendering error by properly formatting range objects
                  const range = armedAction.action?.range || armedAction.range;
                  if (typeof range === 'string') return range;
                  if (typeof range === 'object' && range?.normal && range?.long) {
                    return `${range.normal}/${range.long} ft`;
                  }
                  if (typeof range === 'number') return `${range} ft`;
                  return String(range);
                })()}</span>}
              </div>
            </div>
            {objectTargeting && selectedTargets.length === 0 && !multiattackTarget && (
              <ObjectTargetNameField
                value={objectNameInput}
                onChange={setObjectNameInput}
                onConfirm={handleConfirmTargeting}
              />
            )}
            {resourceSpendControl && (
              <div className="target-picker resource-spend-picker">
                <label htmlFor="action-resource-spend">{resourceSpendControl.label}</label>
                <input
                  id="action-resource-spend"
                  aria-label={resourceSpendControl.label}
                  type="number"
                  min={resourceSpendControl.exhausted ? 0 : resourceSpendControl.min}
                  max={resourceSpendControl.max}
                  step="1"
                  value={resourceSpend}
                  disabled={resourceSpendControl.exhausted}
                  onChange={(event) => setResourceSpend(event.target.value)}
                />
                <small>{resourceSpendControl.max} point{resourceSpendControl.max === 1 ? '' : 's'} available.</small>
              </div>
            )}
            <div className="targeting-status">
              {/* @date_of_change 2025-09-10 - CRITICAL FIX: Prevent double-counting targets */}
              {/* During multiattack, target can be in both selectedTargets and multiattackTarget */}
              <span>{objectTargeting ? 'Objects' : 'Targets'} Selected: {isInMultiattackMode ? (multiattackTarget ? 1 : 0) : selectedTargets.length}</span>
              {(selectedTargets.length > 0 || multiattackTarget || (objectTargeting && objectNameInput.trim())) && (
                <button 
                  className="confirm-targeting-btn"
                  onClick={handleConfirmTargeting}
                  disabled={resourceSpendControl?.exhausted === true}
                >
                  Execute Action
                </button>
              )}
              <button 
                className="cancel-targeting-btn"
                onClick={onCancelArmed}
              >
                Cancel
              </button>
            </div>
            {!objectTargeting && typeof onTargetSelect === 'function' && (
              <div className={`targeting-map-prompt ${targetChoiceState.rangeFiltered && availableTargetOptions.length === 0 ? 'is-blocked' : ''}`} role="status">
                <span aria-hidden="true" className="targeting-map-prompt__sigil"><Crosshair size={14} /></span>
                <span>{targetPrompt}</span>
              </div>
            )}
            {!objectTargeting && availableTargetOptions.length > 0 && (
              <details className="target-picker target-picker--fallback">
                <summary aria-label="Show fallback target list">
                  {availableTargetOptions.length === 1 ? 'Can’t click the target?' : 'Can’t click a token?'}
                </summary>
                <div className="target-picker__options" role="group" aria-label="Choose a legal target">
                  {availableTargetOptions.map((target) => (
                    <button
                      key={target.id}
                      type="button"
                      className={target.selected ? 'is-selected' : ''}
                      aria-pressed={target.selected}
                      onClick={() => {
                        if (target.selected && typeof onTargetDeselect === 'function') {
                          onTargetDeselect(target.id);
                        } else {
                          onTargetSelect(target.id);
                        }
                      }}
                    >
                      <span>{target.label}</span>
                      {target.selected && <span className="target-picker__selected">Selected</span>}
                    </button>
                  ))}
                </div>
                <small>{targetChoiceState.rangeFiltered ? 'Only legal, in-range targets are shown.' : 'Use this list only when map tokens overlap or cannot be clicked.'}</small>
              </details>
            )}
            {cunningStrikeAvailable && (
              <div className="cunning-strike-selection">
                <label htmlFor="cunning-strike-effect">Cunning Strike (optional)</label>
                <select
                  id="cunning-strike-effect"
                  value={cunningStrikeEffect}
                  onChange={(event) => setCunningStrikeEffect(event.target.value)}
                >
                  <option value="">Keep all Sneak Attack dice</option>
                  {Object.values(CUNNING_STRIKE_EFFECTS).map((effect) => (
                    <option
                      key={effect.key}
                      value={effect.key}
                      disabled={effect.key === 'trip' && (!tripEligibility.valid || !tripEligibility.allowed)}
                    >
                      {effect.label} — spend {effect.diceCost}d6
                    </option>
                  ))}
                </select>
                <small>
                  The selected die is removed before rolling Sneak Attack damage. The effect resolves only if the attack hits and deals Sneak Attack.
                </small>
              </div>
            )}
          </div>
        )}

        {/* Multiattack Sequence */}
        {isInMultiattackMode && (
          <div className="multiattack-sequence-section">
            <h4>Multiattack Sequence</h4>
            <div className="sequence-display">
              {multiattackSequence.length > 0 ? (
                multiattackSequence.map((action, index) => (
                  <div key={index} className="sequence-action">
                    <span className="sequence-number">{index + 1}.</span>
                    <span className="action-name">{action.name}</span>
                    <span className="targets">({action.targets?.length || 0} targets)</span>
                  </div>
                ))
              ) : (
                <div className="sequence-empty">No actions in sequence</div>
              )}
            </div>
            <div className="sequence-controls">
              {multiattackSequence.length > 0 && (
                <>
                  <button 
                    className="execute-sequence-btn"
                    onClick={onExecuteMultiattack}
                  >
                    Execute All ({multiattackSequence.length})
                  </button>
                  <button 
                    className="clear-sequence-btn"
                    onClick={onClearMultiattack}
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Target Selection Status + Magic Missile allocation (if applicable) */}
        {(selectedTargets.length > 0 || multiattackTarget) && (
          <div className="selected-targets-section">
            <h4>Selected Targets</h4>
            <div className="targets-list">
              {selectedTargets.map((t, index) => {
                const tid = (t && typeof t === 'object') ? t.id : t;
                const tname = resolveTargetName(t); // @date_of_change 2025-09-14 - Use helper to resolve names
                const resolvedToken = (t && typeof t === 'object') ? t : tokens.find((token) => token.id === tid);
                const ttype = resolvedToken?.type || (resolvedToken?.isSceneObject ? 'object' : 'creature');
                return (
                  <div key={index} className="target-entry">
                    <div className="target-item">
                      <span className="target-name">{tname}</span>
                      <span className="target-type">({ttype})</span>
                      {/* @date_of_change 2025-09-14 - Magic Missile dart allocation controls */}
                      {resolverMeta && resolverMeta.kind === 'auto_hit_multi_dart' && (
                        <span className="mm-allocation">
                          <button className="btn btn-xs" onClick={() => adjustAllocation(tid, -1)}>-</button>
                          <span className="mm-count">{allocation[tid] || 0}</span>
                          <button className="btn btn-xs" onClick={() => adjustAllocation(tid, +1)}>+</button>
                        </span>
                      )}
                    </div>
                    {/* @date_of_change 2026-07-11 - Expected advantage / Sneak Attack adjudication */}
                    {renderAttackPreview(tid)}
                  </div>
                );
              })}
              {multiattackTarget && (
                <div className="target-entry">
                  <div className="target-item multiattack-target">
                    <span className="target-name">{multiattackTarget.name}</span>
                    <span className="target-type">({multiattackTarget.type}) [Multiattack]</span>
                  </div>
                  {/* @date_of_change 2026-07-11 - Expected advantage / Sneak Attack adjudication */}
                  {renderAttackPreview(multiattackTarget.id)}
                </div>
              )}
            </div>
            {/* Summary for multi-dart spells */}
            {resolverMeta && resolverMeta.kind === 'auto_hit_multi_dart' && (
              <div className="mm-summary">
                {(() => {
                  const slotLevel = resolverMeta.slotLevel || 1;
                  const totalDarts = (resolverMeta.multiComponent?.baseCount || 0) + Math.max(0, slotLevel - 1) * (resolverMeta.multiComponent?.perSlot || 0);
                  const allocSum = Object.values(allocation).reduce((s, n) => s + (n || 0), 0);
                  return (
                    <>
                      <strong>Magic Missile:</strong> {totalDarts} darts at level {slotLevel} — Allocated {allocSum}/{totalDarts}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* @date_of_change 2025-09-14 - Collapsible Instructions */}
        <div className="instructions">
          <button
            type="button"
            className="instructions-toggle"
            onClick={() => setInstructionsExpanded(!instructionsExpanded)}
            aria-expanded={instructionsExpanded}
          >
            Targeting Instructions
          </button>
          {instructionsExpanded && (
            <ul>
              <li>{objectTargeting ? 'Choose the object this action uses.' : 'Click an in-range token on the map to target it.'}</li>
              <li>{objectTargeting ? 'Click an object token on the map, or enter the name of an object without a token.' : 'Open the fallback target list only if tokens overlap or cannot be clicked. Use Ctrl or Shift for multi-select when the action supports it.'}</li>
              <li>Magic Missile: adjust per-target dart counts with the allocation controls. Darts must sum to the total at the chosen slot.</li>
              <li>Select Execute Action when ready, or Cancel to abort.</li>
              <li>Pin this panel to the left sidebar if you need it to remain visible.</li>
            </ul>
          )}
          {!armedAction && (
            <div className="no-action-message">
              <em>Use the Actions panel to select an action that requires targeting</em>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (renderInline || typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

MultiattackTargetingPopup.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onTogglePin: PropTypes.func,
  onPositionChange: PropTypes.func,
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  isPinned: PropTypes.bool,
  renderInline: PropTypes.bool,
  currentCombatant: PropTypes.object,
  availableActions: PropTypes.array,
  selectedTargets: PropTypes.array,
  multiattackTarget: PropTypes.object,
  targetingEnabled: PropTypes.bool,
  armedAction: PropTypes.object,
  tokens: PropTypes.array, // @date_of_change 2025-09-14 - Added for target name resolution
  combatState: PropTypes.object, // @date_of_change 2026-07-11 - Grid/token context for attack preview
  onArmAction: PropTypes.func,
  onConfirmArmed: PropTypes.func,
  onCancelArmed: PropTypes.func,
  onTargetSelect: PropTypes.func,
  onTargetDeselect: PropTypes.func,
  multiattackSequence: PropTypes.array,
  isInMultiattackMode: PropTypes.bool,
  onStartMultiattack: PropTypes.func,
  onAddToMultiattack: PropTypes.func,
  onExecuteMultiattack: PropTypes.func,
  onClearMultiattack: PropTypes.func,
  // @date_of_change 2026-07-12 - Inline manual-roll step props
  pendingManualRoll: PropTypes.shape({
    visible: PropTypes.bool,
    title: PropTypes.string,
    prompt: PropTypes.string,
    numDice: PropTypes.number,
    dieSize: PropTypes.number
  }),
  onManualRollConfirm: PropTypes.func,
  onManualRollCancel: PropTypes.func
};

export default MultiattackTargetingPopup;
